/**
 * 파일명 : evl0111m.tsx
 * 설명 : 프로젝트평가 매핑관리
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2025-12-18   김한철    최초작성
 */

/************************************************
 * Import 영역
 ************************************************/
/* React */
import React, { useEffect, useMemo, useCallback, useState } from "react";

/* AntD */
import {
  Table,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Row,
  Col,
  message,
  Upload,
  Popconfirm,   // ✅ 추가
  Modal,
  Switch
} from "antd";
import { SearchOutlined, UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

/* 업무 */
import * as commonProxy from "libs/CommonProxy";
import * as common from "libs/common";

/* Redux */
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { Evl0111mAction } from "../../../redux/slice/evl/evl0111m_slice";
import type { RawProjectMapRow, ProjectEvaluationRow } from "../../../redux/models/evl/evl0111m_model";

/************************************************
 * 유틸
 ************************************************/
function buildCodeMap(datas: any[]): Record<string, string> {
  const map: Record<string, string> = {};
  (datas ?? []).forEach((d: any) => {
    const code = d?.cd ?? d?.CD ?? d?.code ?? d?.value;
    const name = d?.cdNm ?? d?.CD_NM ?? d?.name ?? d?.label;
    if (code != null) map[String(code)] = name ?? String(code);
  });
  return map;
}


function isSubmittedPj(row: any): boolean {
  return String(row?.pmSubYn ?? row?.PM_SUB_YN ?? "")
    .trim()
    .toUpperCase() === "Y";
}
/************************************************
 * Props
 * - evlYear/evlYearList는 0109m 컨테이너에서 내려받는다.
 * - (현재는 0109에서 프로젝트 탭은 로컬 state로 분리됨)
 ************************************************/
export type ProjectEvaluationTableProps = {
  evlYear: string;
  setEvlYear: React.Dispatch<React.SetStateAction<string>>;
  evlYearList: string[];
  setEvlYearList: React.Dispatch<React.SetStateAction<string[]>>;

  // ✅ evl0109m에서 내려주는 공통 props
  evlCycleExists: boolean;
  hrEvlStDt: string;
  eval2EdDt: string;
  isMappingModifyBlocked: boolean;
  lockMsg: string;
};

/************************************************
 * [프로젝트 평가 탭 컴포넌트]
 * ✅ year list는 TREVLR0007로 가져온다(인사 조회 트랜코드 재사용)
 * ✅ 프로젝트 목록은 TREVLR0008로 조회한다(기존 유지)
 ************************************************/
export function ProjectEvaluationTable({
  evlYear,
  setEvlYear,
  evlYearList,
  setEvlYearList,
   // ✅ 추가
  evlCycleExists,
  hrEvlStDt,
  eval2EdDt,
  isMappingModifyBlocked,
  lockMsg,
}: ProjectEvaluationTableProps) {
  const dispatch = useAppDispatch();

  /************************************************
   * ✅ 0111m 고유 상태
   ************************************************/
  const rows = useAppSelector((state) => state.Evl0111m.rows);
  const loading = useAppSelector((state) => state.Evl0111m.loading);
  const posClsMap = useAppSelector((state) => state.Evl0111m.posClsMap);
  const searchText = useAppSelector((state) => state.Evl0111m.searchText);
  const keyField = useAppSelector((state) => state.Evl0111m.keyField);
  const statusFilter = useAppSelector((state) => state.Evl0111m.statusFilter);
  const query = useAppSelector((state) => state.Evl0111m.query);
  const posName = useCallback((cd?: string) => (cd ? posClsMap[cd] ?? cd : ""), [posClsMap]);
  const [pageNo, setPageNo] = useState(1);
  // ✅ 업로드 미리보기 모달 상태
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPreviewFileName, setUploadPreviewFileName] = useState("");
  const [uploadPreviewRows, setUploadPreviewRows] = useState<any[]>([]);
  const [onlyExcluded, setOnlyExcluded] = useState(false); // ✅ 제외만 보기
  

  /************************************************
   * ✅ 공통코드(직급) 세팅
   ************************************************/
  const setPosClsList = useCallback(() => {
    const option = {
      useYn: "Y",
      callback: (datas: any[]) => dispatch(Evl0111mAction.setPosClsMap(buildCodeMap(datas))),
    };
    common.getComCodeList("011", option);
  }, [dispatch]);

  /************************************************
   * ✅ Row normalize
   ************************************************/
  const normalizeRow = useCallback((r: RawProjectMapRow, idx: number): ProjectEvaluationRow => {
    const pjMapId = String(r.pjMapId ?? r.PJ_MAP_ID ?? "");
    const evlYearV = String(r.evlYear ?? r.EVL_YEAR ?? "");

    const empNo = String(r.empNo ?? r.EMP_NO ?? "");
    const projectCd = String(r.projectCd ?? r.PROJECT_CD ?? "");
    const eeDeptCd = String(r.eeDeptCd ?? r.EE_DEPT_CD ?? "");
    const eePosClsCd = String(r.eePosClsCd ?? r.EE_POS_CLS_CD ?? "");

    const pmEvlEmpNo = String(r.pmEvlEmpNo ?? r.PM_EVL_EMP_NO ?? "");
    const pmEvlPosClsCd = String(r.pmEvlPosClsCd ?? r.PM_EVL_POS_CLS_CD ?? "");
    const pmEvlDeptCd = String(r.pmEvlDeptCd ?? r.PM_EVL_DEPT_CD ?? "");

    const pjNm = String(r.pjNm ?? r.PJ_NM ?? "");
    const empNm = String(r.empNm ?? r.USER_NM ?? "");
    const eedeptNm = String(r.eedeptNm ?? r.EEDEPT_NM ?? "");
    const pmEmpNm = String(r.pmEmpNm ?? r.PM_EMP_NM ?? "");
    const pmDeptNm = String(r.pmDeptNm ?? r.PM_DEPT_NM ?? "");

    const pmEvlRmk = String(r.pmEvlRmk ?? r.PM_EVL_RMK ?? "");

    const pmEvlModEdDt = String(r.pmEvlModEdDt ?? r.PM_EVL_MOD_ED_DT ?? "");
    const pmSubYn = String(r.pmSubYn ?? r.PM_SUB_YN ?? "N");
    const evlCd = String((r as any).evlCd ?? (r as any).EVL_CD ?? "");

    const eeUpperDeptNm = String(r.eeUpperDeptNm ?? r.EE_UPPER_DEPT_NM ?? "");
    const key = pjMapId || String(idx);

    const eval1EmpNm = String((r as any).eval1EmpNm ?? (r as any).EVAL1_EMP_NM ?? "");
    const eval2EmpNm = String((r as any).eval2EmpNm ?? (r as any).EVAL2_EMP_NM ?? "");

    return {
      key,
      pjMapId,
      evlYear: evlYearV,

      empNo,
      projectCd,
      eeDeptCd,
      eePosClsCd,

      pmEvlEmpNo,
      pmEvlPosClsCd,
      pmEvlDeptCd,

      pjNm,
      empNm,
      eedeptNm,
      pmEmpNm,
      pmDeptNm,

      pmEvlRmk,
      pmEvlModEdDt,
      evlCd,

      pmSubYn,
      eeUpperDeptNm,
      eval1EmpNm,
      eval2EmpNm,
    };
  }, []);

    // ✅ 매핑 변경 가능 여부 (0111 공통)
    const canModifyMapping = useMemo(() => {
      // 평가정보 없으면 매핑 변경도 막고 싶으면 여기 포함
      // return evlCycleExists && !isMappingModifyBlocked;
      return !isMappingModifyBlocked;
    }, [isMappingModifyBlocked /*, evlCycleExists */]);

    const showBlockedMsg = useCallback(() => {
      message.warning(lockMsg || "현재 평가 진행 상태에서는 매핑을 변경할 수 없습니다.");
    }, [lockMsg]);

  /************************************************
   * ✅ (추가) 평가년도 목록은 TREVLR0007로 가져오기
   ************************************************/
  const loadYearListFromHr = useCallback(async () => {
    try {
      const requestData: any = {
        svcId: "TREVLR0007",
        evlYear: evlYear,
        searchKey: "all",
        searchText: "",
      };

      await commonProxy.callAsyncSvc(requestData, {
        txCd: "TREVLR0007",
        success: (responseData: any) => {
          const rawEvlList = responseData?.evlList ?? responseData?.data?.evlList ?? [];
          const years = (Array.isArray(rawEvlList) ? rawEvlList : [])
            .map((y) => String(y))
            .filter((y) => y.trim() !== "")
            .sort((a, b) => Number(b) - Number(a));

          setEvlYearList(years);

          if (years.length > 0 && (!evlYear || !years.includes(String(evlYear)))) {
            setEvlYear(years[0]);
          }
        },
      });
    } catch {
      // ignore
    }
  }, [evlYear, setEvlYear, setEvlYearList]);


  /************************************************
   * ✅ 프로젝트 조회 (TREVLR0008)
   ************************************************/
  const loadProjectMapping = useCallback(
    async (opt?: {
      showMsg?: boolean;
      params?: { keyField: string; searchText: string; statusFilter: string };
    }) => {
      dispatch(Evl0111mAction.setLoading(true));
      try {
        const p = opt?.params ?? query;

        const requestData: any = {
          svcId: "TREVLR0008",
          evlYear,

          searchKey: p.keyField,
          searchText: (p.searchText ?? "").trim(),
          statusFilter: p.statusFilter,
        };

        await commonProxy.callAsyncSvc(requestData, {
          txCd: "TREVLR0008",
          success: (responseData: any) => {
            const rawList =
              responseData?.list ??
              responseData?.data?.list ??
              responseData?.projectMappingList ??
              responseData?.data?.projectMappingList ??
              responseData?.pjMappingList ??
              responseData?.data?.pjMappingList ??
              [];

            const arr: RawProjectMapRow[] = Array.isArray(rawList) ? rawList : [];
            const mapped = arr.map(normalizeRow);

            dispatch(Evl0111mAction.setRows(mapped));

            if (opt?.showMsg !== false) {
              // 🔥 여기만 조건 분기
              if (mapped.length === 0) {
                message.info("조회된 데이터가 없습니다.");
              } else {
                message.success(`조회 완료 (${mapped.length}건)`);
              }
            }
          },
        });
      } catch (e) {
        message.error("조회 중 오류가 발생했습니다.");
      } finally {
        dispatch(Evl0111mAction.setLoading(false));
      }
    },
    [dispatch, evlYear, normalizeRow, query]
  );

  const deleteProjectMappingOne = useCallback(
  async (row: ProjectEvaluationRow) => {
    // ✅ 상태 기반 차단
    if (!canModifyMapping) {
      showBlockedMsg();
      return;
    }
    if (!row) return;

    // ✅ 제출 완료면 삭제 불가
    if (isSubmittedPj(row)) {
      message.warning("이미 제출된 데이터는 삭제할 수 없습니다.");
      return;
    }

    try {
      common.showLoading(true);

      const requestData: any = {
        svcId: "TREVLS0025",
        pjMapId: row.pjMapId,     // ✅ 이 파일의 고유키
        evlYear: row.evlYear,     // (서버가 필요하면 같이)
        empNo: row.empNo,
        projectCd: row.projectCd,
      };

      await commonProxy.callAsyncSvc(requestData, {
        txCd: "TREVLS0025",
        success: async () => {
          message.success("삭제되었습니다.");
          // ✅ 여기(0111)는 재조회 함수가 loadProjectMapping임
          await loadProjectMapping({ showMsg: false });
        },
      });
    } catch (e) {
       
      message.error("삭제 중 오류가 발생했습니다.");
    } finally {
      common.showLoading(false);
    }
  },
  [loadProjectMapping]
);

  // ✅ 현재 화면 기준 "평가 진행중(업로드 제외)" 대상
  // 기준: 동일 프로젝트코드 + 피평가자사번 조합이며, 이미 제출된 건
  const startedPjKeySet = useMemo(() => {
    const set = new Set<string>();

    (rows ?? []).forEach((r: ProjectEvaluationRow) => {
      if (!r) return;

      // 현재 파일 기준으로는 제출완료(Y)를 진행중/잠금 대상으로 본다
      if (isSubmittedPj(r)) {
        const key = `${String(r.projectCd ?? "").trim()}__${String(r.empNo ?? "").trim()}`;
        set.add(key);
      }
    });

    return set;
  }, [rows]);

  // ✅ PJ템플릿 파싱 → 미리보기 rows 생성 (서버 호출 없음)
  const parsePjExcelForPreview = useCallback(async (file: File) => {
    const ab = await file.arrayBuffer();
    const wb = XLSX.read(ab, { type: "array" });

    const wsTpl = wb.Sheets["PJ템플릿"];
    if (!wsTpl) throw new Error("시트 'PJ템플릿' 을 찾을 수 없습니다.");

    const tplRows = XLSX.utils.sheet_to_json<any>(wsTpl, {
      range: 1,
      header: [
        "프로젝트명",
        "PM(평가자)",
        "피평가자명",
        "사업부",
        "실/팀",
        "PM여부",
        "1차평가자",
        "2차평가자",
        "프로젝트코드",
        "피평가자사번",
        "PM(평가자)사번",
      ],
      defval: "",
      raw: false,
    });

    // ✅ 비어있는 행 제거 (코드/사번/PM여부 중 하나라도 있으면 남김)
    const filtered = (tplRows ?? []).filter((r: any) => {
      const projectCd = String(r["프로젝트코드"] ?? "").trim();
      const empNo = String(r["피평가자사번"] ?? "").trim();
      const pmEmpNo = String(r["PM(평가자)사번"] ?? "").trim();
      const pmYn = String(r["PM여부"] ?? "").trim();
      return projectCd || empNo || pmEmpNo || pmYn;
    });

    const previewRows = filtered.map((r: any, idx: number) => {
      const rowNo = idx + 2;

      const projectCd = String(r["프로젝트코드"] ?? "").trim();
      const empNo = String(r["피평가자사번"] ?? "").trim();
      const pmEvlEmpNo = String(r["PM(평가자)사번"] ?? "").trim();
      const pmYn = String(r["PM여부"] ?? "").trim(); // 템플릿엔 PM여부(Y/N)로 들어오는 케이스가 많음

      // ✅ 표시용
      const pjNm = String(r["프로젝트명"] ?? "").trim();
      const pmEmpNm = String(r["PM(평가자)"] ?? "").trim();
      const empNm = String(r["피평가자명"] ?? "").trim();
      const upperDeptNm = String(r["사업부"] ?? "").trim();
      const deptNm = String(r["실/팀"] ?? "").trim();
      const eval1Nm = String(r["1차평가자"] ?? "").trim();
      const eval2Nm = String(r["2차평가자"] ?? "").trim();

      // ✅ 프론트 필수 검증 (필요한 것만)
      const errors: string[] = [];

      // 🔴 평가 진행중이면 매핑 변경 불가
      if (isMappingModifyBlocked) {
        errors.push(lockMsg || "현재 평가 진행 상태에서는 매핑을 변경할 수 없습니다.");
      }

      if (!projectCd) errors.push("프로젝트코드 누락");
      if (!empNo) errors.push("피평가자사번 누락");
      if (!pmEvlEmpNo) errors.push("PM(평가자)사번 누락");
      if (!pmYn) errors.push("PM여부 누락");

      // ✅ 현재 평가 진행중 건 제외
      const startedKey = `${projectCd}__${empNo}`;
      if (projectCd && empNo && startedPjKeySet.has(startedKey)) {
        errors.push("평가 진행중");
      }

      // ✅ 서버로 보낼 값 (기존 로직 유지: pmYn을 서버에서 변환한다고 했으니 그대로)
      return {
        key: `${file.name}-${rowNo}`,
        _rowNo: rowNo,
        evlYear,
        projectCd,
        empNo,
        pmEvlEmpNo,
        pmYn,

        // 미리보기 표시값
        pjNm,
        pmEmpNm,
        empNm,
        upperDeptNm,
        deptNm,
        eval1Nm,
        eval2Nm,

        _errors: errors,
        _valid: errors.length === 0,
      };
    });

    return previewRows;
  }, [evlYear, isMappingModifyBlocked, lockMsg, startedPjKeySet]);

  const commitPjExcelUpload = useCallback(async () => {
    if (!canModifyMapping) {
      showBlockedMsg();
      return;
    }
    if (!uploadPreviewRows.length) {
      message.warning("업로드할 데이터가 없습니다.");
      return;
    }
    // ✅ 인사매핑처럼: 오류행은 제외하고 업로드하고 싶으면 이 방식
    const targets = uploadPreviewRows.filter(r => r._valid);
    if (targets.length === 0) {
      message.warning("업로드 가능한 데이터가 없습니다. (모든 행에 오류가 있습니다.)");
      return;
    }
    try {
      setUploading(true);
      common.showLoading(true);

      const requestData: any = {
        svcId: "TREVLS0012",
        list: targets.map((r: any) => ({
          _rowNo: r._rowNo,      // 서버에서 안 쓰면 제거 가능
          evlYear: r.evlYear,
          projectCd: r.projectCd,
          empNo: r.empNo,
          pmEvlEmpNo: r.pmEvlEmpNo,
          pmYn: r.pmYn,          // 기존 유지
        })),
      };

      await commonProxy.callAsyncSvc(requestData, {
        txCd: "TREVLS0012",
        success: async () => {
          message.success(`엑셀 업로드 완료 (${targets.length}건)`);
          setUploadPreviewOpen(false);
          setUploadPreviewRows([]);
          setUploadPreviewFileName("");
          setOnlyExcluded(false); // ✅ 추가
          await loadProjectMapping({ showMsg: false });
        },
      });
    } catch (e) {
       
      message.error("엑셀 업로드 중 오류가 발생했습니다.");
    } finally {
      setUploading(false);
      common.showLoading(false);
    }
}, [uploadPreviewRows, loadProjectMapping, canModifyMapping, showBlockedMsg]);

  /************************************************
   * ✅ 프로젝트 평가 매핑 쿼리 생성 (TREVLS0009)
   ************************************************/
  const onCreateProjectMappingQuery = useCallback(async () => {
    try {
      if (!canModifyMapping) {
        showBlockedMsg();
        return;
     }
      common.showLoading(true);

      const requestData: any = {
        svcId: "TREVLS0009",
        evlYear,
      };
      await commonProxy.callAsyncSvc(requestData, {
        txCd: "TREVLS0009",

        success: async (response: any) => {
          const data = response?.data ?? response ?? {};

          const precheckOk = data?.precheckOk ?? false;
          const cnt = data?.count ?? 0;
          if (!precheckOk) {
            message.warning("먼저 인사평가 매핑을 생성해야 합니다.");
            return;
          }

          if (cnt > 0) {
            message.success(`프로젝트 평가 매핑이 ${cnt}건 생성되었습니다.`);
            await loadProjectMapping({ showMsg: false });
          } else {
            message.info("추가로 생성할 프로젝트 평가 매핑이 없습니다.");
          }
        },

        error: (errorMsg: string) => {
          message.error(errorMsg || "서버 통신 중 오류가 발생했습니다.");
        },
      });
    } catch (e) {
      message.error("프로젝트 매핑 쿼리 생성 중 오류가 발생했습니다.");
    } finally {
      common.showLoading(false);
    }
  }, [evlYear, canModifyMapping, showBlockedMsg]);

  const evlGbNm = (evlCd?: string) => {
    const cd = String(evlCd ?? "").trim();
    if (cd === "03") return "PM";
    if (cd === "04") return "팀원";
    return "-";
  };

  /************************************************
   * ✅ 템플릿 다운로드 (0111m: 프로젝트 평가 매핑관리용)
   * - "서식" 컬럼 기준으로 평가구분 결정
   ************************************************/
  const onDownloadTemplate = useCallback(async () => {
    try {
      common.showLoading(true);

      const requestData: any = { svcId: "TREVLR0020" };

      await commonProxy.callAsyncSvc(requestData, {
        txCd: "TREVLR0020",
        success: (res: any) => {
          const data = res?.data ?? res ?? {};
          const userList = data?.userList ?? [];
          const projectList = data?.projectList ?? [];

          // ✅ 1) 템플릿 시트(화면 rows 기반)
          const templateHeaderKor = [
            "프로젝트명",
            "PM(평가자)",
            "피평가자명",
            "사업부",
            "실/팀",
            "PM여부",
            "1차평가자",
            "2차평가자",
            "프로젝트코드",
            "피평가자사번",
            "PM(평가자)사번",
          ];

          const templateAoaRows = (rows ?? []).map((r: ProjectEvaluationRow) => {
            return [
              r.pjNm ?? "",
              r.pmEmpNm ?? "",
              r.empNm ?? "",
              (r as any).eeUpperDeptNm ?? "",
              r.eedeptNm ?? "",
              (r.evlCd === "03" ? "Y" : "N"),
              r.eval1EmpNm,
              r.eval2EmpNm,
              r.projectCd ?? "",
              r.empNo ?? "",
              r.pmEvlEmpNo ?? "",
            ];
          });

          // ✅ Workbook
          const wb = XLSX.utils.book_new();

          // ✅ 템플릿 시트
          const wsTemplate = XLSX.utils.aoa_to_sheet([templateHeaderKor]);
          if (templateAoaRows.length > 0) {
            XLSX.utils.sheet_add_aoa(wsTemplate, templateAoaRows, { origin: "A2" });
          }
          // ✅ 템플릿 컬럼 너비 (표시값 + 코드성)
          wsTemplate["!cols"] = [
            { wch: 45 }, // 프로젝트명
            { wch: 16 }, // PM(평가자)
            { wch: 14 }, // 피평가자명
            { wch: 18 }, // 사업부
            { wch: 18 }, // 실/팀
            { wch: 10 }, // PM여부
            { wch: 16 }, // 1차평가자
            { wch: 16 }, // 2차평가자
            { wch: 16 }, // 프로젝트코드
            { wch: 14 }, // 피평가자사번
            { wch: 14 }, // PM(평가자)사번
          ];

          XLSX.utils.book_append_sheet(wb, wsTemplate, "PJ템플릿");

          // ✅ 한글 헤더 박기(코드성 시트용)
          const setKoreanHeader = (ws: XLSX.WorkSheet, headerRow: string[]) => {
            XLSX.utils.sheet_add_aoa(ws, [headerRow], { origin: "A1" });
          };

          // ✅ 2) 사원목록(업로드 매핑용)
          const users = (userList ?? []).map((u: any) => ({
            USER_NM: u.userNm ?? u.USER_NM ?? "",
            DEPT_NM: u.deptNm ?? u.DEPT_NM ?? "",
            POST_NM: u.postNm ?? u.POST_NM ?? "",
            EMP_NO: u.empNo ?? u.EMP_NO ?? "",
          }));

          const wsUsers = XLSX.utils.json_to_sheet(users, {
            header: ["USER_NM",  "DEPT_NM", "POST_NM","EMP_NO"],
          });
          setKoreanHeader(wsUsers, ["사원명", "팀", "직급명", "사원번호"]);
          XLSX.utils.book_append_sheet(wb, wsUsers, "사원목록");

          // ✅ 3) 프로젝트목록(업로드 매핑용)
          const projects = (projectList ?? []).map((p: any) => ({
            PROJECT_NM: p.projectNm ?? p.PROJECT_NM ?? p.pjNm ?? p.PJ_NM ?? "",
            PM_USER_NM: p.pmUserNm ?? p.PM_USER_NM ?? "",
            PJ_START_DT: p.pjStartDt ?? p.PJ_START_DT ?? "",   // ✅ 추가
            PJ_END_DT: p.pjEndDt ?? p.PJ_END_DT ?? "",         // ✅ 추가
            PROJECT_CD: p.projectCd ?? p.PROJECT_CD ?? p.pjCd ?? p.PJ_CD ?? "",
            PM_EMP_NO: p.pmEmpNo ?? p.PM_EMP_NO ?? "",
          }));

          const wsProjects = XLSX.utils.json_to_sheet(projects, {
             header: ["PROJECT_NM", "PM_USER_NM", "PJ_START_DT", "PJ_END_DT","PROJECT_CD", "PM_EMP_NO" ],
          });
          setKoreanHeader(wsProjects, [ "프로젝트명", "PM이름","시작일","종료일","프로젝트코드","PM사번",]);
          XLSX.utils.book_append_sheet(wb, wsProjects, "프로젝트목록");

          const now = new Date();
          const y = now.getFullYear();
          const m = String(now.getMonth() + 1).padStart(2, "0");
          const d = String(now.getDate()).padStart(2, "0");
          const fileName = `프로젝트_평가_매핑템플릿_${y}${m}${d}.xlsx`;
          const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
          const blob = new Blob([wbout], { type: "application/octet-stream" });
          saveAs(blob, fileName);

          message.success(`PJ템플릿 다운로드 완료 (${templateAoaRows.length}건)`);
        },
      });
    } catch (e) {
       
      message.error("템플릿 다운로드 중 오류가 발생했습니다.");
    } finally {
      common.showLoading(false);
    }
  }, [rows, evlYear, posName]);

  const previewInvalidCount = useMemo(
  () => uploadPreviewRows.filter((r) => !r._valid).length,
  [uploadPreviewRows]
);
const previewValidCount = useMemo(
  () => uploadPreviewRows.filter((r) => r._valid).length,
  [uploadPreviewRows]
);

const previewTableRows = useMemo(() => {
  return onlyExcluded ? uploadPreviewRows.filter((r) => !r._valid) : uploadPreviewRows;
}, [onlyExcluded, uploadPreviewRows]);

const previewColumns: ColumnsType<any> = useMemo(() => [
  { title: "행", dataIndex: "_rowNo", width: 70, align: "center" },
  { title: "프로젝트명", dataIndex: "pjNm", width: 220, align: "center" }, 
  { title: "PM(평가자)", dataIndex: "pmEmpNm", width: 120, align: "center" },
  { title: "피평가자명", dataIndex: "empNm", width: 120, align: "center" },

  {
    title: "서식",
    dataIndex: "pmYn",
    width: 90,
    align: "center",
    render: (v: string) => {
      if (v === "Y") return "PM";
      if (v === "N") return "팀원";
      return "-";
    },
  },
  {
    title: "확인",
    width: 220,
    align: "center",
    render: (_: any, r: any) => {
      if (r._valid) return <Tag color="success">유효</Tag>;
      return (
        <span style={{ color: "#cf1322" }}>
          {(r._errors ?? []).join(", ")}
        </span>
      );
    },
  },
], []);

  /************************************************
 * ✅ 엑셀 다운로드(데이터용) - 컬럼 순서 변경
 ************************************************/
const onDownloadExcel = useCallback(async () => {
  try {
    common.showLoading(true);

    if (!rows || rows.length === 0) {
      message.warning("다운로드할 데이터가 없습니다.");
      return;
    }

    // ✅ 변경된 컬럼 순서
    const headerKor = [
      "프로젝트명",
      "PM(평가자)",
      "피평가자명",
      "사업부",
      "실/팀",
      "서식",
      "1차평가자",
      "2차평가자",
    ];

    const aoaRows = rows.map((r) => {
      return [
        r.pjNm ?? "",                    // 프로젝트명
        r.pmEmpNm ?? "",                 // PM(평가자)
        r.empNm ?? "",                   // 피평가자명
        r.eeUpperDeptNm ?? "",           // 사업부
        r.eedeptNm ?? "",                // 실/팀
        (r.evlCd === "03" ? "PM" : "팀원"),  // 서식 (EVL_CD 기준: 03=PM, 04=팀원)
        r.eval1EmpNm,                    // 1차평가자 
        r.eval2EmpNm,                    // 2차평가자 
      ];
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headerKor, ...aoaRows]);

    // ✅ 컬럼 너비도 순서에 맞게 조정
    ws["!cols"] = [
      { wch: 45 }, // 프로젝트명
      { wch: 16 }, // PM(평가자)
      { wch: 14 }, // 피평가자명
      { wch: 18 }, // 사업부
      { wch: 18 }, // 실/팀
      { wch: 10 }, // 서식
      { wch: 16 }, // 1차평가자
      { wch: 16 }, // 2차평가자
    ];

    XLSX.utils.book_append_sheet(wb, ws, "매핑");

    const fileName = `프로젝트평가_매핑_${evlYear || new Date().getFullYear()}.xlsx`;
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, fileName);

    message.success(`엑셀 다운로드 완료 (${aoaRows.length}건)`);
  } catch (e) {
    message.error("엑셀 다운로드 중 오류가 발생했습니다.");
  } finally {
    common.showLoading(false);
  }
}, [rows, evlYear]);

  /************************************************
   * ✅ 최초: 공통코드 로딩
   ************************************************/
  useEffect(() => {
    setPosClsList();
  }, [setPosClsList]);

  /************************************************
   * ✅ 최초 1회: yearList 확보 + 프로젝트 조회
   ************************************************/
  useEffect(() => {
    (async () => {
      await loadYearListFromHr();
      await loadProjectMapping({ showMsg: false });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /************************************************
   * ✅ 검색 버튼: 입력값을 query로 확정 + 조회
   ************************************************/
  const onClickSearch = useCallback(async () => {
    const next = { keyField, searchText, statusFilter };
    setPageNo(1);

    dispatch(Evl0111mAction.setQuery(next));
    await loadProjectMapping({ showMsg: true, params: next });
  }, [dispatch, keyField, searchText, statusFilter, loadProjectMapping]);

  /************************************************
   * ✅ 검색 초기화: 입력+query 초기화 후 조회
   ************************************************/
  const onClickReset = useCallback(async () => {
    setPageNo(1);
    dispatch(Evl0111mAction.resetSearch());

    await loadProjectMapping({
      params: {
        keyField: keyField,     // 기본 검색필드 (현재 기본값 유지)
        searchText: "",         // 🔥 검색어 제거
        statusFilter: "",       // 🔥 상태 필터 초기화 (기본이 전체라면 "")
      },
      showMsg: false,
    });
  }, [dispatch, loadProjectMapping, keyField]);

  /************************************************
   * ✅ PM 평가자 팝업
   ************************************************/
  const openPmPopup = useCallback(
    (record: ProjectEvaluationRow) => {
        if (!canModifyMapping) {
          showBlockedMsg();
          return;
        }
      const accept = async (targets: any[]) => {
        const picked = targets?.[0];
        if (!picked?.apprEmpNo) {
          message.warning("선택된 사용자가 없습니다.");
          return;
        }

        const doSave = async () => {
          const requestData = {
            svcId: "TREVLS0014",
            pjMapId: record.pjMapId,
            newPmEmpNo: picked.apprEmpNo,
          };

          try {
            common.showLoading(true);
            await commonProxy.callAsyncSvc(requestData, {
              txCd: "TREVLS0014",
              success: async () => {
                message.success("PM 평가자 저장 완료");
                await loadProjectMapping({ showMsg: false });
              },
            });
          } catch (e) {
             
            message.error("PM 평가자 저장 중 오류가 발생했습니다.");
          } finally {
            common.showLoading(false);
          }
        };

        // ✅ 선택 이후 경고 (제출완료 건일 때만)
        if (record.pmSubYn === "Y") {
          Modal.confirm({
            title: "PM 평가가 이미 제출 완료된 건입니다.",
            content: `평가자를 변경하면 기존 평가 결과가 영향을 받을 수 있습니다. 계속 진행하시겠습니까?`,
            okText: "변경",
            cancelText: "취소",
            centered: true,
            onOk: doSave, // 확인 → 저장
          });
          return; // 취소 시 아무것도 안 함
        }

        // ✅ 미제출 건은 바로 저장
        await doSave();
      };

      common.openPopup({
        title: "PM 평가자 선택",
        popupId: "evl0109p",
        accept,
        cancel: () => {},
        width: 1350,
        props: {
          data: {
            mode: "EVL_EVALUATOR_PICK",
            which: "pm",
            currentEmpNo: record.pmEvlEmpNo,
            eeEmpNo: record.empNo,
            eeUserNm: record.empNm,
          },
        },
      } as any);
    },
    [loadProjectMapping]
  );

  /************************************************
   * ✅ 컬럼
   ************************************************/
  const columns: ColumnsType<ProjectEvaluationRow> = useMemo(
    () => [
      {
        title: "평가구분",
        dataIndex: "evlCd",   // ✅ EVL_CD가 여기로 들어와야 함
        key: "evlCd",
        width: 90,
        align: "center",
        render: (v: string) => evlGbNm(v),
      },
      {
        title: "프로젝트",
        key: "projectBlock",
        width: 260,
        fixed: "left",
        align: "center",
        render: (_: any, r: ProjectEvaluationRow) => (
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 16 }}>{r.pjNm || "-"}</div>
          </div>
        ),
      },
      {
        title: "피평가자",
        key: "evaluateeBlock",
        width: 180,
        align: "center",
        render: (_: any, r: ProjectEvaluationRow) => {
          const pos = r.eePosClsCd ? posName(r.eePosClsCd) || "-" : "-";
          return (
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontSize: 16 }}>{r.empNm || "-"}</div>
              <div style={{ fontSize: 12, color: "#888" }}>{pos}</div>
            </div>
          );
        },
      },
      {
        title: "사업부",
        dataIndex: "eeUpperDeptNm",
        key: "eeUpperDeptNm",
        width: 160,
        align: "center",
        render: (v: string) => v || "-",
      },
      {
        title: "실/팀",
        dataIndex: "eedeptNm",
        key: "eedeptNm",
        width: 160,
        align: "center",
        render: (v: string) => v || "-",
      },
      {
        title: "평가자(PM)",
        key: "pmBlock",
        width: 220,
        align: "center",
        render: (_: any, r: ProjectEvaluationRow) => {
          const pos = r.pmEvlPosClsCd ? posName(r.pmEvlPosClsCd) || "-" : "-";
          const dept = r.pmDeptNm || "-";
          const nm = r.pmEmpNm || r.pmEvlEmpNo || "지정";

          return (
            <Button
              type="link"
              onClick={() => (canModifyMapping ? openPmPopup(r) : showBlockedMsg())}
              style={{ padding: 0 }}
            >
              <div style={{ lineHeight: 1.2 }}>
                <div style={{ fontSize: 16 }}>{nm}</div>
                <div style={{ fontSize: 13, color: "#666" }}>
                  {dept} / {pos}
                </div>
              </div>
            </Button>
          );
        },
      },
      {
        title: "1차평가자",
        dataIndex: "eval1EmpNm",
        key: "eval1EmpNm",
        width: 120,
        align: "center",
      },
      {
        title: "2차평가자",
        dataIndex: "eval2EmpNm",
        key: "eval2EmpNm",
        width: 120,
        align: "center",
      },
      {
        title: "제출여부",
        dataIndex: "pmSubYn",
        key: "pmSubYn",
        width: 120,
        fixed: "right",
        align: "center",
        render: (v: string) => (v === "Y" ? <Tag color="success">제출완료</Tag> : <Tag>미제출</Tag>),
      },
      {
        title: "관리",
        key: "action",
        width: 90,
        fixed: "right",
        align: "center",
        render: (_: any, r: ProjectEvaluationRow) => {
          const disabled = isSubmittedPj(r) || !canModifyMapping;

          return (
            <Popconfirm
              title="해당 매핑을 삭제하시겠습니까?"
              okText="삭제"
              cancelText="취소"
              onConfirm={() => deleteProjectMappingOne(r)}
              disabled={disabled}
            >
              <Button danger size="small" disabled={disabled}>
                삭제
              </Button>
            </Popconfirm>
          );
        },
      },
    ],
    [openPmPopup, posName]
  );

  const yearOptions = useMemo(() => {
    if (!evlYearList || evlYearList.length === 0) return [];
    return evlYearList.map((y) => ({ label: y, value: y }));
  }, [evlYearList]);

  const onChangeYear = useCallback(
    (v: any) => {
      setEvlYear(String(v));
    },
    [setEvlYear]
  );

  return (
    <div>
      {/* 통계 */}
        <Row style={{ marginBottom: 10 }}>
          <Col flex="auto">
            {evlCycleExists ? (
            <Space wrap>
              <Tag color="blue">평가정보 등록됨</Tag>
              <Tag>인사평가 시작일자: {hrEvlStDt || "-"}</Tag>
              <Tag>인사평가 종료일자: {eval2EdDt || "-"}</Tag>
              {isMappingModifyBlocked && <Tag color="red">{lockMsg}</Tag>}
            </Space>
          ) : (
            <Tag color="red">평가정보가 등록되지 않았습니다.</Tag>
          )}
        </Col>
        </Row>

      {/* 검색 + 우측 엑셀 기능 */}
      <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 16 }}>
        {/* 왼쪽: 검색/필터 */}
        <Col flex="auto">
          <Space wrap>
            <Select value={evlYear} onChange={onChangeYear} style={{ width: 110 }} options={yearOptions} />

            <Select
              value={keyField}
              onChange={(v) => dispatch(Evl0111mAction.setKeyField(String(v)))}
              style={{ width: 180 }}
            >
              <Select.Option value="all">전체</Select.Option>
              <Select.Option value="pjNm">프로젝트명</Select.Option>
              <Select.Option value="empNm">피평가자명</Select.Option>
              <Select.Option value="eedeptNm">피평가자 부서명</Select.Option>
              <Select.Option value="pmEmpNm">PM 평가자명</Select.Option>
              <Select.Option value="pmDeptNm">PM 부서명</Select.Option>
            </Select>

            <Input
              placeholder="검색어 입력"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => dispatch(Evl0111mAction.setSearchText(e.target.value))}
              style={{ width: 240 }}
              allowClear
              onPressEnter={onClickSearch}
            />

            <Button type="primary" icon={<SearchOutlined />} onClick={onClickSearch} loading={loading}>
              검색
            </Button>

            <Button onClick={onClickReset} disabled={loading}>
              초기화
            </Button>

            <Button 
              onClick={onCreateProjectMappingQuery}
              loading={loading}
              disabled={isMappingModifyBlocked}
            >
              매핑 자동생성
            </Button>
          </Space>
        </Col>

        {/* 오른쪽: 엑셀 기능 */}
        <Col flex="none">
          <Space wrap style={{ justifyContent: "flex-end" }}>
            <Upload
              accept=".xlsx,.xls"
              showUploadList={false}
              beforeUpload={async (file) => {
                // ✅ 상태 기반 차단 (파일 선택 단계에서 막기)
                if (!canModifyMapping) {
                  showBlockedMsg();
                  return false;
                }

                try {
                  const rows = await parsePjExcelForPreview(file as File);
                  if (!rows.length) {
                    message.warning("업로드할 데이터가 없습니다. (템플릿 A2부터 확인)");
                    return false;
                  }
                  setUploadPreviewFileName(file.name);
                  setUploadPreviewRows(rows);
                  setUploadPreviewOpen(true);
                } catch (e: any) {
                   
                  message.error(e?.message || "엑셀 파싱 중 오류가 발생했습니다.");
                }
                return false; // ✅ 업로드는 직접 처리
              }}
            >
              <Button
                className="excel-btn"
                icon={<UploadOutlined />}
                disabled={!canModifyMapping}
                title={!canModifyMapping ? (lockMsg || "매핑 변경이 불가합니다.") : undefined}
              >
                템플릿 업로드
              </Button>
            </Upload>

            <Button className="excel-btn" icon={<DownloadOutlined />} onClick={onDownloadTemplate} loading={loading}>
              템플릿 다운로드
            </Button>

            <Button className="excel-btn" icon={<DownloadOutlined />} onClick={onDownloadExcel} loading={loading}>
              엑셀 다운로드
            </Button>
          </Space>
        </Col>
      </Row>

      {/* 테이블 */}
      <Table
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey="key"
        scroll={{ x: 1100 }}
        pagination={{
          current: pageNo,          // ✅ 컨트롤
          pageSize: 10,
          showSizeChanger: false,
          showTotal: (t) => `총 ${t}명`,
          onChange: (page) => setPageNo(page), // ✅ 페이지 클릭 시 반영
        }}
        bordered
        rowClassName={(record) => {
          const cls: string[] = [];
          if (record.pmSubYn === "Y") cls.push("submitted-row");
          if (record.evlCd === "03") cls.push("pm-row");
          return cls.join(" ");
        }}
      />
      {/* ✅ 업로드 미리보기 모달 (프로젝트) */}
      <Modal
        open={uploadPreviewOpen}
        title={`엑셀 업로드 미리보기 - ${uploadPreviewFileName}`}
        width={1050}
        centered
        okText="업로드"
        cancelText="취소"
        okButtonProps={{
          loading: uploading,
          // ✅ 오류행 제외 업로드 정책이면: 업로드 대상이 0이면 막기
          disabled: previewValidCount === 0,
        }}
        onOk={commitPjExcelUpload}
        onCancel={() => {
          if (uploading) return;
          setUploadPreviewOpen(false);
          setUploadPreviewRows([]);
          setUploadPreviewFileName("");
          setOnlyExcluded(false); // ✅ 추가
        }}
      >
      <Space style={{ marginBottom: 12 }} wrap>
        <Tag>총 {uploadPreviewRows.length}건</Tag>
        <Tag color={previewValidCount > 0 ? "success" : "default"}>업로드 대상 {previewValidCount}건</Tag>
        <Tag color={previewInvalidCount > 0 ? "warning" : "success"}>제외 {previewInvalidCount}건</Tag>

        <Switch
          checked={onlyExcluded}
          onChange={setOnlyExcluded}
          checkedChildren="제외만"
          unCheckedChildren="전체"
          disabled={previewInvalidCount === 0}
        />

        {previewInvalidCount > 0 && (
          <span style={{ color: "#ad6800" }}>오류가 있는 행은 제외하고 업로드됩니다.</span>
        )}
      </Space>

        <Table
          size="small"
          rowKey="key"
          dataSource={previewTableRows}
          columns={previewColumns}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
          }}
          scroll={{ x: 900 }}
        />
      </Modal>
            

      <style>{`
        .ant-table-thead > tr > th {
          text-align: center !important;
        }

        /* ✅ 엑셀 버튼 초록 톤 */
        .excel-btn {
          border-color: #52c41a !important;
          color: #389e0d !important;
        }
        .excel-btn:hover,
        .excel-btn:focus {
          border-color: #73d13d !important;
          color: #52c41a !important;
        }

        .pm-row > td {
          background-color: #fffbe6 !important; /* 연한 노랑 */
        }
        .pm-row:hover > td {
          background-color: #fff1b8 !important; /* hover 진하게 */
        }
      `}</style>
    </div>
  );
}
